import { computed, Directive, input } from '@angular/core';
import { cn } from '../../utils/class.utils';

/**
 * `@spartan-ng/brain` no publica una primitiva `table`, así que — igual que Helm — se
 * estilan los elementos nativos, que ya son accesibles y semánticos.
 */

abstract class StyledElement {
  // eslint-disable-next-line @angular-eslint/no-input-rename -- permite class="..." en el host, mergeado con cn()
  readonly userClass = input<string>('', { alias: 'class' });
  protected abstract baseClass(): string;
  protected readonly _computedClass = computed(() => cn(this.baseClass(), this.userClass()));
}

@Directive({
  selector: 'table[appTable]',
  standalone: true,
  host: { '[class]': '_computedClass()' },
})
export class TableDirective extends StyledElement {
  protected override baseClass(): string {
    return 'w-full caption-bottom border-collapse text-sm text-text';
  }
}

@Directive({
  selector: 'thead[appTableHead]',
  standalone: true,
  host: { '[class]': '_computedClass()' },
})
export class TableHeadDirective extends StyledElement {
  protected override baseClass(): string {
    return 'bg-surface-2 [&_tr]:border-b [&_tr]:border-border';
  }
}

@Directive({
  selector: 'tbody[appTableBody]',
  standalone: true,
  host: { '[class]': '_computedClass()' },
})
export class TableBodyDirective extends StyledElement {
  protected override baseClass(): string {
    return '[&_tr:last-child]:border-0';
  }
}

@Directive({
  selector: 'tr[appTableRow]',
  standalone: true,
  host: { '[class]': '_computedClass()' },
})
export class TableRowDirective extends StyledElement {
  protected override baseClass(): string {
    return 'border-b border-border transition-colors last:border-0 hover:bg-surface-2/40';
  }
}

@Directive({
  selector: 'th[appTableHeaderCell]',
  standalone: true,
  host: { '[class]': '_computedClass()' },
})
export class TableHeaderCellDirective extends StyledElement {
  protected override baseClass(): string {
    return 'h-11 px-4 text-left align-middle text-xs font-bold tracking-[0.08em] whitespace-nowrap text-heading-strong uppercase';
  }
}

@Directive({
  selector: 'td[appTableCell]',
  standalone: true,
  host: { '[class]': '_computedClass()' },
})
export class TableCellDirective extends StyledElement {
  readonly truncate = input(true);

  protected override baseClass(): string {
    return this.truncate()
      ? 'px-4 py-3.5 align-middle truncate max-w-48'
      : 'px-4 py-3.5 align-middle whitespace-nowrap';
  }
}

export const TABLE_DIRECTIVES = [
  TableDirective,
  TableHeadDirective,
  TableBodyDirective,
  TableRowDirective,
  TableHeaderCellDirective,
  TableCellDirective,
] as const;
